import React, { useCallback, useState, useRef } from "react";
import "./YearSelectSlider.css";

interface MonthSliderProps {
  value: number;
  months: number[];
  handleMonthSliderChange: (event: Event, newValue: number | number[]) => void;
}

const MonthSelectSlider: React.FC<MonthSliderProps> = ({ 
  value, 
  handleMonthSliderChange, 
  months
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLInputElement>(null);
  
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  
  // Move useCallback BEFORE any returns
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value);
    if (index >= 0 && index < months.length) {
      const event = new Event('change', { bubbles: true });
      handleMonthSliderChange(event, index);
    }
  }, [months, handleMonthSliderChange]);
  
  const handleMouseDown = () => {
    setIsDragging(true);
    setShowTooltip(true);
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setShowTooltip(false), 100);
  };
  
  const getMonthLabel = (index: number) => {
    if (index >= 0 && index < months.length) {
      const monthValue = months[index];
      return monthNames[monthValue - 1] || monthValue.toString();
    }
    return '';
  };
  
  // Safety check for empty array - AFTER all hooks
  if (!months || months.length === 0) {
    return <div className="month-section">No months available</div>;
  }
  
  const currentIndex = months.indexOf(value);
  const safeIndex = currentIndex !== -1 ? currentIndex : 0;
  
  const minMonth = Math.min(...months);
  const maxMonth = Math.max(...months);
  
  // Calculate percentage for positioning
  const percentage = months.length > 1 ? (safeIndex / (months.length - 1)) * 100 : 0;
  
  return (
    <div className="month-section">
      <span className="filter-section-title">
        Month <span className="month-range">
          ({monthNames[minMonth - 1] || minMonth}-{monthNames[maxMonth - 1] || maxMonth})
        </span>
      </span>
      
      <div className="mui-style-slider-container">
        <input
          ref={sliderRef}
          type="range"
          className="mui-style-slider-input"
          min={0}
          max={months.length - 1}
          step={1}
          value={safeIndex}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => !isDragging && setShowTooltip(false)}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
        />
        
        <div className="mui-style-slider-track-container">
          {/* Rail (gray background) */}
          <div className="mui-style-slider-rail" />
          
          {/* Active track (turquoise) */}
          <div 
            className="mui-style-slider-track" 
            style={{ width: `${percentage}%` }}
          />
          
          {/* Marks */}
          {months.map((_, index) => {
            const markPercentage = months.length > 1 ? (index / (months.length - 1)) * 100 : 0;
            return (
              <div
                key={index}
                className={`mui-style-slider-mark ${index <= safeIndex ? 'active' : ''}`}
                style={{ left: `${markPercentage}%` }}
              />
            );
          })}
          
          {/* Thumb */}
          <div 
            className={`mui-style-slider-thumb ${isDragging ? 'dragging' : ''}`}
            style={{ left: `${percentage}%` }}
          >
            {(showTooltip || isDragging) && (
              <div className="mui-style-slider-tooltip">
                {getMonthLabel(safeIndex)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthSelectSlider;